/**
 * SSR-safe debounce с накоплением числовых значений
 *
 * В отличие от обычного debounce, который использует последнее значение,
 * этот debounce накапливает значения по выбранной стратегии (max/min).
 *
 * Полезно для случаев когда нужно обработать агрегированное значение из множества событий,
 * например: загрузка секций при быстром скролле (max), минимизация индекса (min).
 *
 * На клиенте: накапливает значения и вызывает функцию с задержкой
 * На сервере: вызывает функцию сразу без задержки
 *
 * @example
 * ```typescript
 * // Накопление максимума (для загрузки секций)
 * const updateIndex = ssrSafeNumberDebounce((newIndex: number) => {
 *   sectionVisitedIndex.value = newIndex;
 * }, 150, 'max');
 *
 * updateIndex(5);  // Запланирован вызов с 5
 * updateIndex(8);  // Обновлен до 8 (max)
 * updateIndex(6);  // Остается 8 (max)
 * // Через 150ms: вызов с 8 (максимальное значение)
 *
 * // Накопление минимума (для других случаев)
 * const updateMinIndex = ssrSafeNumberDebounce((index: number) => {
 *   minVisibleIndex.value = index;
 * }, 150, 'min');
 *
 * updateMinIndex(5);  // Запланирован вызов с 5
 * updateMinIndex(3);  // Обновлен до 3 (min)
 * updateMinIndex(7);  // Остается 3 (min)
 * // Через 150ms: вызов с 3 (минимальное значение)
 * ```
 */

type AccumulationStrategy = 'max' | 'min';

type NumberDebouncedFunc = {
  (value: number): void;
  cancel(): void;
  flush(): void;
};

/**
 * SSR-safe debounce с накоплением числовых значений
 *
 * @param func - Функция которая принимает число (накопленное значение)
 * @param wait - Задержка в миллисекундах (игнорируется на сервере)
 * @param strategy - Стратегия накопления: 'max' (максимум) или 'min' (минимум)
 * @returns Debounced функция с накоплением значений
 */
export function ssrSafeNumberDebounce(
  func: (value: number) => void,
  wait: number = 0,
  strategy: AccumulationStrategy = 'max'
): NumberDebouncedFunc {
  // На сервере возвращаем функцию которая вызывает сразу
  if (process.env.SERVER) {
    const immediateFunc = ((value: number) => {
      func(value);
    }) as NumberDebouncedFunc;

    immediateFunc.cancel = () => {};
    immediateFunc.flush = () => {};

    return immediateFunc;
  }

  // На клиенте реализуем debounce с накоплением по стратегии
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let accumulatedValue: number | null = null;

  const debouncedFunc = ((value: number) => {
    // Обновляем накопленное значение по стратегии
    if (accumulatedValue === null) {
      accumulatedValue = value;
    } else {
      if (strategy === 'max') {
        accumulatedValue = Math.max(accumulatedValue, value);
      } else {
        accumulatedValue = Math.min(accumulatedValue, value);
      }
    }

    // Сбрасываем предыдущий таймер
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    // Устанавливаем новый таймер
    timeoutId = setTimeout(() => {
      if (accumulatedValue !== null) {
        func(accumulatedValue);
        accumulatedValue = null;
        timeoutId = null;
      }
    }, wait);
  }) as NumberDebouncedFunc;

  // Отменить запланированный вызов
  debouncedFunc.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
      accumulatedValue = null;
    }
  };

  // Немедленно вызвать с накопленным значением
  debouncedFunc.flush = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (accumulatedValue !== null) {
      func(accumulatedValue);
      accumulatedValue = null;
    }
  };

  return debouncedFunc;
}

// Экспортируем старое имя для обратной совместимости
export const ssrSafeMaxDebounce = (
  func: (value: number) => void,
  wait: number = 0
): NumberDebouncedFunc => {
  return ssrSafeNumberDebounce(func, wait, 'max');
};
